/**
 * Which panel the monthly vibe card screen shows, as a pure decision.
 *
 * This exists because of a measured blank screen: the screen chose its panel
 * through a chain of scattered `status === …` conditionals, and two of the
 * eight members of `MonthlyVibeCardStatus` — `unavailable` and `collecting` —
 * matched none of them. On staging the feature flag was off, so the API
 * returned `unavailable` for EVERY month, and every user got a header over an
 * empty body. A null card (what the loader's silent `catch` leaves behind on
 * any API failure) did the same.
 *
 * A chain of conditionals cannot be checked for completeness; a total function
 * can. `tests/mobile/vibe-card-panel.test.ts` drives this with every member of
 * the union — read out of `types/index.ts`, so adding a status there without
 * handling it here fails the suite rather than shipping another blank screen.
 *
 * No runtime imports on purpose: the root `node --test` suites cannot load
 * anything that pulls in `react-native`, and the type import below is erased.
 */
import type { MonthlyVibeCardStatus } from "@/types";

export type VibeCardPanel =
  /** The finished card, plus save/share. */
  | "preview"
  /** The month is complete and eligible: the reveal button, the only AI spend. */
  | "reveal"
  | "generating"
  | "failed"
  /** The month ended without enough moments in the jar. */
  | "insufficient"
  /** The reveal window closed and the jar behind it was cleared. */
  | "expired"
  /** The month is still running — nothing to reveal yet. */
  | "collecting"
  /** No card is possible: a future month, or the kill switch is off. */
  | "unavailable"
  /** Nothing is known: the load failed, or a status this build predates. */
  | "unknown";

export function vibeCardPanel(
  status: MonthlyVibeCardStatus | null | undefined,
  { hasContent = false, revealing = false } = {},
): VibeCardPanel {
  // `revealing` outranks the status because the reveal round trip is slower
  // than a render: the button must keep its spinner while the request is in
  // flight, rather than flicking to another panel and back.
  if (revealing) return "reveal";

  switch (status) {
    case "available":
      // Content is what the preview renders; without it there is nothing to
      // show, and falling through to `unknown` says so instead of drawing an
      // empty frame.
      return hasContent ? "preview" : "unknown";
    case "ready":
      return "reveal";
    case "generating":
      return "generating";
    case "failed":
      return "failed";
    case "insufficient_data":
      return "insufficient";
    case "expired":
      return "expired";
    case "collecting":
      return "collecting";
    case "unavailable":
      return "unavailable";
    default:
      // Covers null/undefined (the load failed) and anything added to the
      // union later. Never nothing.
      return "unknown";
  }
}
